/**
 * User-facing names for the routed model families.
 *
 * Duplicated from `src/modelRouting.ts` on purpose: this module is imported by
 * every view and must stay dependency-free (the routing module reads env vars).
 * Only the display names live here — the routes themselves are server-side.
 */
const FAMILY_NAMES: Record<string, string> = {
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
};

/**
 * Human label for a model id.
 *
 * A routed family id ("family:chatgpt") becomes the assistant name; anything
 * else is a concrete OpenRouter id and loses its provider prefix
 * ("openai/gpt-4o" -> "gpt-4o"). An unknown family short name is capitalized so
 * a route added server-side still reads as a name, never as a raw id.
 */
export function formatModelLabel(model: string): string {
  if (model.startsWith("family:")) {
    const key = model.slice("family:".length);
    if (key) return FAMILY_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
  }
  return model.split("/").pop() || model;
}

/**
 * Formats a compact label for one or more model IDs.
 *
 * @param models Model IDs to summarize.
 * @return Compact model summary label.
 */
export function formatModelSummary(models: string[]): string {
  if (models.length === 0) return "No models";
  if (models.length === 1) return formatModelLabel(models[0]!);

  return `${models.length} models`;
}

/** Label for an N-runs-per-day cadence, e.g. `10× per day (every 2.4h)`. */
export function formatTimesPerDay(n: number): string {
  return `${n}× per day (every ${Number((24 / n).toFixed(1))}h)`;
}

/**
 * Average the numeric values in a score map, rounded to the nearest integer.
 * Null/undefined entries are ignored. Returns null when there are no numbers.
 */
export function averageScore(scores: Record<string, number | null>): number | null {
  const values = Object.values(scores).filter(
    (value): value is number => typeof value === "number",
  );
  if (values.length === 0) return null;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}
